// The net-worth figure card from `#s01`/`#s02`: "Net worth · partial · as of
// today 09:12", the dotted-underline figure, the degradation line ("2
// accounts stale, 1 never reported a balance · oldest balance 12 Aug"), and
// the Today/Week/Month deltas. Renders one of three shapes (§6.3):
// `complete` (a plain figure), `partial` (dotted, with counts and the
// oldest as-of -- never hidden, never silently summed as if complete), or
// `unavailable` (the honest inline state, no number at all).
import type { Account } from '@/api/hooks/useAccounts';
import type { NetWorthDelta } from '@/api/hooks/useNetWorthSeries';
import { AmountText } from '@/components/AmountText';
import { DeltaText } from '@/components/DeltaText';
import { HonestInline } from '@/components/HonestInline';
import { formatHumanDate } from '@/lib/date';
import { computeNetWorthRollup } from './netWorthRollup';

export interface NetWorthHeaderDeltas {
  today: NetWorthDelta;
  week: NetWorthDelta;
  month: NetWorthDelta;
}

/**
 * Returns the oldest reporting date represented by the rollup. A rollup made
 * from balances reported on different days is only as current as its oldest
 * known input, so this avoids presenting a fetch timestamp as freshness.
 */
export function formatBalanceAsOf(accounts: Account[]): string | null {
  const dates = accounts
    .filter((account) => account.balance !== null && account.as_of !== null)
    .map((account) => account.as_of as string)
    .sort();
  if (dates.length === 0) return null;

  return `Oldest reported balance ${formatHumanDate(dates[0])}`;
}

export function NetWorthHeader({
  accounts,
  deltas,
  className,
  aggregateDisplay = 'physical',
}: {
  accounts: Account[];
  deltas?: NetWorthHeaderDeltas;
  className?: string;
  aggregateDisplay?: 'physical' | 'canonical_unavailable';
}) {
  if (aggregateDisplay === 'canonical_unavailable') {
    return (
      <div
        className={['min-w-0', className].filter(Boolean).join(' ')}
        data-testid="net-worth-header"
        data-shape="canonical-unavailable"
      >
        <div className="text-xs text-muted-foreground">Net worth is unavailable</div>
        <div className="mt-1 text-sm text-degraded-foreground">Account history total is unavailable.</div>
      </div>
    );
  }

  const rollup = computeNetWorthRollup(accounts);

  if (rollup.shape === 'unavailable') {
    return <HonestInline kind="stale-snapshot" className={className} />;
  }

  const partial = rollup.shape === 'partial';
  const label = partial ? 'Net worth · partial' : 'Net worth';
  const balanceAsOf = formatBalanceAsOf(accounts);
  const knownWithoutReportingTime = accounts.filter((account) => account.balance !== null && account.as_of === null).length;

  return (
    <div className={['min-w-0', className].filter(Boolean).join(' ')} data-testid="net-worth-header" data-shape={rollup.shape}>
      <div className="text-xs text-muted-foreground">
        {label}
        {balanceAsOf ? ` · ${balanceAsOf}` : ' · Balance reporting time unavailable'}
      </div>
      <div className="mt-1 min-w-0 max-w-full text-figure">
        <AmountText value={String(rollup.total)} variant="balance" balanceStatus={partial ? 'stale' : 'ok'} />
      </div>
      {partial && (
        <p className="mt-1.5 text-xs text-degraded-foreground" data-testid="net-worth-degradation">
          {rollup.staleCount > 0 && `${rollup.staleCount} account${rollup.staleCount === 1 ? '' : 's'} stale`}
          {rollup.staleCount > 0 && rollup.neverReportedCount > 0 && ', '}
          {rollup.neverReportedCount > 0 &&
            `${rollup.neverReportedCount} never reported a balance`}
          {rollup.oldestAsOf && ` · oldest balance ${formatHumanDate(rollup.oldestAsOf)}`}
        </p>
      )}
      {knownWithoutReportingTime > 0 && (
        <p className="mt-1.5 text-xs text-degraded-foreground" data-testid="net-worth-reporting-time-unknown">
          {knownWithoutReportingTime} known balance{knownWithoutReportingTime === 1 ? '' : 's'} reporting time unavailable
        </p>
      )}
      {deltas && (
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <div>
            <div className="text-muted-foreground">Today</div>
            <DeltaText value={deltas.today.value} percent={deltas.today.percent} direction={sign(deltas.today.value)} />
          </div>
          <div>
            <div className="text-muted-foreground">Week</div>
            <DeltaText value={deltas.week.value} percent={deltas.week.percent} direction={sign(deltas.week.value)} />
          </div>
          <div>
            <div className="text-muted-foreground">Month</div>
            <DeltaText value={deltas.month.value} percent={deltas.month.percent} direction={sign(deltas.month.value)} />
          </div>
        </div>
      )}
    </div>
  );
}

function sign(value: string): 'up' | 'down' | 'flat' {
  const n = Number(value);
  return n > 0 ? 'up' : n < 0 ? 'down' : 'flat';
}

export default NetWorthHeader;
